import { Suspense, type ReactElement } from 'react';
import { createBrowserRouter, Navigate, useLocation, type RouteObject } from 'react-router-dom';
import { AppShell } from './AppShell';
import { RequireAuth } from './RequireAuth';
import { RouteError } from './RouteError';
import { lazyRoute } from './chunkReload';
import { Login } from '@/features/auth/Login';
import { Dashboard } from '@/routes/Dashboard';
import { More } from '@/routes/More';
import { NotFound } from '@/routes/NotFound';
import { Privacy } from '@/routes/Privacy';
import { DeleteAccount } from '@/routes/DeleteAccount';
import { RouteFallback } from './RouteFallback';

/**
 * Route table.
 *
 * `Vehicles` is lazy-loaded so its code and its TanStack Query wiring land in a
 * separate async chunk, fetched only when the user navigates to /vehicles. FCP
 * is already over budget (PLAN §Risks); keeping this route out of the main
 * bundle means the login critical path does not grow by a byte.
 *
 * `basename` derives from `import.meta.env.BASE_URL`, which Vite sets from the
 * single `base` in vite.config.ts. The base lives in exactly one place.
 */
const Vehicles = lazyRoute(() =>
  import('@/routes/Vehicles').then((m) => ({ default: m.Vehicles })),
);

// The live map is lazy for the same reason, and it matters more: maplibre-gl is
// ~230 kB gzipped, so the shell/login path must never load it. The route chunk
// pulls MapCanvas, which in turn lazily pulls maplibre — two hops, both off the
// critical path (PLAN §"the known risk").
const LiveMap = lazyRoute(() =>
  import('@/routes/LiveMap').then((m) => ({ default: m.LiveMap })),
);

// Vehicle detail (canvas 1c) is lazy for the same reason as the list: it pulls
// the live model and its socket transport, none of which the login/shell path
// should carry.
const VehicleDetail = lazyRoute(() =>
  import('@/routes/VehicleDetail').then((m) => ({ default: m.VehicleDetail })),
);

// Trip replay (canvas 1d) is its own chunk: it pulls maplibre AND recharts, and
// neither belongs on any path a user reaches before asking for a replay.
const TripReplay = lazyRoute(() =>
  import('@/routes/TripReplay').then((m) => ({ default: m.TripReplay })),
);

// The top-level Trips page (canvas 1e) is lazy for the same reason as replay —
// it embeds the very same replay panel (maplibre + recharts) in its right pane.
const Trips = lazyRoute(() => import('@/routes/Trips').then((m) => ({ default: m.Trips })));

// Zones (canvas 5c; renamed from Geofences in slice 24) is lazy: it pulls
// maplibre AND terra-draw (a new dependency), neither of which the login/shell
// critical path may carry. The built entry chunk is asserted to contain no
// terra-draw (seam S5).
const Zones = lazyRoute(() =>
  import('@/routes/Zones').then((m) => ({ default: m.Zones })),
);

// Places (slice 24) is lazy for the same reason as Zones: it pulls maplibre AND
// terra-draw, neither of which the login/shell critical path may carry. There is
// NO canvas artboard for this screen — all 51 checked — so it is patterned on
// Zones rather than drawn to pixels; see the header of `routes/Places.tsx`.
const Places = lazyRoute(() => import('@/routes/Places').then((m) => ({ default: m.Places })));

// The fleet Alerts feed (canvas 1o) is lazy: its right pane pulls maplibre (the
// alert map), which the login/shell critical path must not carry.
const Alerts = lazyRoute(() => import('@/routes/Alerts').then((m) => ({ default: m.Alerts })));

// Settings (canvas 1r) is lazy for the ordinary reason — it is a rarely-visited
// screen and has no business sitting in the shell's entry chunk. It pulls no map.
const Settings = lazyRoute(() =>
  import('@/routes/Settings').then((m) => ({ default: m.Settings })),
);

// Reports (canvas 1p, slice 26) is lazy: it is a document screen the login/shell
// critical path never needs, and it carries the report catalogue + summary
// wiring. It pulls no map.
const Reports = lazyRoute(() =>
  import('@/routes/Reports').then((m) => ({ default: m.Reports })),
);

// Behaviour (canvas 3b, slice 29) is lazy for the same reason as Reports: a
// document screen the login/shell critical path never needs, pulling no map.
const Behaviour = lazyRoute(() =>
  import('@/routes/Behaviour').then((m) => ({ default: m.Behaviour })),
);

// Maintenance (slice 28) is lazy for the ordinary reason: it is a data screen the
// login/shell critical path never needs, and it pulls no map and no chart.
const Maintenance = lazyRoute(() =>
  import('@/routes/Maintenance').then((m) => ({ default: m.Maintenance })),
);

// Drivers (canvas 3b, slice D1) is lazy for the same reason as Reports/Behaviour:
// a data screen the login/shell critical path never needs, pulling no map.
const Drivers = lazyRoute(() =>
  import('@/routes/Drivers').then((m) => ({ default: m.Drivers })),
);

/**
 * `/t/:token` — THE PUBLIC SHARE PAGE (slice 22).
 *
 * 🔴 IT SITS AT THE TOP LEVEL, OUTSIDE `RequireAuth` AND OUTSIDE `AppShell`.
 * The person opening it is not our customer and has no session; nesting it under
 * `/` would bounce them to a login screen for a link they were told would simply
 * work, and would wrap a stranger's page in the fleet's own navigation.
 *
 * Lazy for the ordinary reason, and for one more: the customer-facing bundle
 * must not grow because of a page our customers never open, and this page must
 * not carry the app's data layer.
 */
// The customer's own Sharing screen (slice 22) — lazy for the ordinary reason.
const Shares = lazyRoute(() => import('@/routes/Shares').then((m) => ({ default: m.Shares })));

const SharedTrack = lazyRoute(() =>
  import('@/routes/SharedTrack').then((m) => ({ default: m.SharedTrack })),
);

/**
 * Route table, exported so tests can build a memory router from the SAME config
 * — the only way to prove the error boundary is actually wired into the tree,
 * not merely rendered in isolation.
 *
 * `errorElement` on the authenticated `/` route covers every child (all lazy
 * routes included) and replaces React Router's default error screen for the
 * whole shell.
 */
/**
 * A redirect that keeps the whole URL, not just the path — `?query` and `#hash`
 * travel to the new location. React Router's `<Navigate to="/x">` replaces the
 * entire location, so anything after the path is discarded silently.
 *
 * Used for the slice-24 `/geofences` → `/zones` rename. See the route below for
 * how the omission was caught.
 */
function RedirectPreservingUrl({ to }: { to: string }): ReactElement {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

export const routes: RouteObject[] = [
    { path: '/login', element: <Login /> },
    {
      path: '/',
      element: (
        <RequireAuth>
          <AppShell />
        </RequireAuth>
      ),
      errorElement: <RouteError />,
      children: [
        { index: true, element: <Dashboard /> },
        {
          path: 'map',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <LiveMap />
            </Suspense>
          ),
        },
        {
          path: 'vehicles',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Vehicles />
            </Suspense>
          ),
        },
        {
          path: 'vehicles/:id',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <VehicleDetail />
            </Suspense>
          ),
        },
        {
          path: 'trips',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Trips />
            </Suspense>
          ),
        },
        {
          path: 'zones',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Zones />
            </Suspense>
          ),
        },
        // Slice 17f — the phone's overflow destination. EAGER, not lazy, and
        // deliberately with no `Suspense`/`RouteFallback` wrapper: it is a static
        // list of two links with nothing to fetch, so a fallback skeleton would
        // flash for no reason, and it imports no maplibre so the entry chunk
        // stays clean. Every other child here is lazy because it pulls a map, a
        // chart, or a data screen; this one pulls neither.
        { path: 'more', element: <More /> },
        // Slice 24 renamed Geofences → Zones. Keep the old path alive as a
        // permanent redirect so an existing bookmark still lands on the screen
        // (AC-7) — `replace` so Back does not bounce between the two paths.
        //
        // 🔴 IT CARRIES THE QUERY STRING AND HASH ACROSS, AND THAT IS NOT A NICETY.
        // A bare `<Navigate to="/zones">` DROPS them, which is exactly how this
        // was found: CI's real-browser map gate navigates to
        // `/geofences?mapdebug=1`, the redirect swallowed `?mapdebug=1`, the debug
        // handle was never attached and the gate failed with "no map handle
        // exposed". The gate was right and the redirect was wrong — a redirect
        // that silently discards half the URL is not the same page.
        //
        // The same loss would hit any real bookmark carrying state, which is the
        // only reason this route exists at all. Preserving them is what makes the
        // old URL equivalent rather than merely adjacent.
        { path: 'geofences', element: <RedirectPreservingUrl to="/zones" /> },
        {
          path: 'places',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Places />
            </Suspense>
          ),
        },
        {
          path: 'alerts',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Alerts />
            </Suspense>
          ),
        },
        {
          path: 'settings',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Settings />
            </Suspense>
          ),
        },
        {
          path: 'maintenance',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Maintenance />
            </Suspense>
          ),
        },
        {
          path: 'reports',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Reports />
            </Suspense>
          ),
        },
        {
          path: 'shares',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Shares />
            </Suspense>
          ),
        },
        {
          path: 'behaviour',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Behaviour />
            </Suspense>
          ),
        },
        {
          path: 'drivers',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <Drivers />
            </Suspense>
          ),
        },
        {
          path: 'vehicles/:id/trips/:tripId',
          element: (
            <Suspense fallback={<RouteFallback />}>
              <TripReplay />
            </Suspense>
          ),
        },
      ],
    },
    {
      // Outside the authenticated tree on purpose — see the comment above.
      path: '/t/:token',
      element: (
        <Suspense fallback={<RouteFallback />}>
          <SharedTrack />
        </Suspense>
      ),
    },
    // 🔴 PUBLIC LEGAL PAGES (slice 81) — outside RequireAuth AND outside AppShell,
    // for the same reason as `/t/:token`: Google Play requires the privacy policy
    // and the account-deletion page to be world-readable URLs, opened by people
    // with no session. Nesting them under `/` would bounce a signed-out visitor
    // to the login screen for a link the Play listing points at directly. They
    // are eager (no map, no data layer — just i18n text), so no Suspense wrapper.
    { path: '/privacy', element: <Privacy /> },
    { path: '/delete-account', element: <DeleteAccount /> },
    { path: '*', element: <NotFound /> },
];

export const router = createBrowserRouter(routes, {
  basename: import.meta.env.BASE_URL,
});
